import React, { useEffect, useState } from 'react';
import {
    closestCenter,
    DndContext,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import { DragEndEvent } from "@dnd-kit/core/dist/types";
import { arrayMove, rectSortingStrategy, SortableContext, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { useNavigate, useParams } from "react-router-dom";
import { Package, Portal } from "../typings";
import EditableInput from "../components/EditableInput";
import { Row } from "../components/Flex";
import {
    afterUploadPreview,
    afterUploadThumbnail,
    deletePackage,
    deletePreview,
    fetchPackage, fetchPortals,
    updatePackage
} from "../package-api";
import FileRow from "./FileRow";
import TextArea from "../components/TextArea";
import { useApi } from "../api-context";

import ImageUploading from 'react-images-uploading';
import { ImageType } from "react-images-uploading/dist/typings";
import { useAuth } from "../auth-context";
import { FaTrash } from "react-icons/fa6";
import { toast } from "react-toastify";
import MultiSelectField from "../components/MultiSelectField";
import SelectField from "../components/SelectField";
import TextField from "../components/TextField";
import Switch from "react-switch";
import ConfirmationDialog from "../tags/ConfirmationDialog";
import CloneDialog from "./CloneDialog";
import AddFilesDialog from "./AddFilesDialog";
import contentCard from "../content/ContentCard";

const TYPES = [
    {value: 'PRO', text: 'PROFESSIONAL VERSION'},
    {value: 'PLAYER', text: 'PLAYER VERSION'},
    {value: 'ALL', text: 'ALL VERSIONS'}
];
const LEVELS = ['PRO', 'SEMIPRO', 'L17_20', 'L15_16', 'L13_14', 'L11_12', 'L08_10', 'UNDER_7'];
const CATEGORIES = ['YOUTH', 'JUNIOR',
    'PROFESIONAL', 'FREE', 'OFF_ICE', 'INITIATION', 'INLINE', 'GOALIE', 'VIDEO_CONTENT', 'ICE_HOCKEY', 'SKILL_DEVELOPMENT'];

function PackagePage() {
    const navigate = useNavigate();
    const api = useApi();
    const { data } = useAuth();
    const [portals, setPortals] = useState<Portal[]>([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [filePackage, setFilePackage] = useState<Package>();
    const [rand, setRand] = useState(Math.random());

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            const oldIndex = filePackage!.files.findIndex(v => v.id === active?.id);
            const newIndex = filePackage!.files.findIndex(v => v.id === over?.id);
            const files = arrayMove(filePackage!.files, oldIndex, newIndex);
            setFilePackage({ ...filePackage!, files });
        }
    };

    let { id } = useParams();

    const loadPackage = (id: any) => {
        fetchPackage(api, id)
            .then((result) => {
                setFilePackage(result);
            })
            .catch((reason) => {
                if (reason !== 'Unauthorized') {
                    toast.error('Failed to load package');
                }
            });
    };

    const onSave = () => updatePackage(api, id!, filePackage!)
        .then(() => toast.success("Successfully updated package"))
        .catch(() => toast.error("Failed to update package"));

    const onDelete = () => deletePackage(api, id!)
        .then(() => {
            toast.success("Successfully deleted package");
            navigate('/group/0');
        })
        .catch(() => toast.error("Failed to delete package"));

    const onUploadThumbnail = (imageList: ImageType[]) => {
        const formData = new FormData();
        var image = imageList[0];
        formData.append("file", image.file!);

        fetch('https://api.hockeycoachvision.com/package/' + id + '/thumbnail', {
            method: 'POST',
            body: formData
        })
            .then(() => afterUploadThumbnail(api, id!).then(() => setRand(Math.random())))
            .catch(() => toast.error('Failed to upload thumbnail'));
    };

    const onUploadPreview = (imageList: ImageType[]) => {
        const formData = new FormData();
        const image = imageList[0];
        formData.append("file", image.file!);

        fetch(`https://api.hockeycoachvision.com/package/${id}/preview`, {
            method: 'POST',
            body: formData
        }).then(res => res
            .text()
            .then(name => afterUploadPreview(api, id!, name).then(() => loadPackage(id)))
        ).catch(() => toast.error('Failed to upload preview'));
    };

    const onDeletePreview = (name: string) => {
        deletePreview(api, id!, name)
            .then(() => loadPackage(id))
            .catch(() => toast.error('Failed to delete preview'));
    };

    useEffect(() => {
        loadPackage(id);
        fetchPortals(api)
            .then(setPortals)
            .catch((reason) => {
                if (reason !== 'Unauthorized') {
                    toast.error('Failed to load portals');
                }
            });
    }, [id]);

    const isAdmin = data?.organisationType === 'ADMIN';

    if (!filePackage) {

        return null;
    }

    const renderPreviews = () => {
        return (
            <div className="box basic-info"
                 style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '12px'
                    }}
                >
                    <div className="info-title">Previews</div>
                    <ImageUploading
                        value={[]}
                        onChange={onUploadPreview}
                        maxNumber={100}
                        dataURLKey="data_url"
                    >
                        {({
                              onImageUpload,
                              dragProps,
                          }) => (
                            <button
                                style={{ flex: 0, minWidth: '100px' }}
                                className="my-button"
                                onClick={onImageUpload}
                                {...dragProps}
                            >
                                Upload
                            </button>
                        )}
                    </ImageUploading>
                </div>
                {
                    filePackage.images ? filePackage.images.split(';')
                            .map(name => {
                                return (
                                    <div style={{ position: 'relative' }}>
                                        <img
                                            style={{
                                                maxWidth: '100%'
                                            }}
                                            src={`https://api.hockeycoachvision.com/package/${id}/file/${name}`}
                                        />
                                        <button
                                            className="icon-button"
                                            onClick={() => onDeletePreview(name)}
                                        >
                                            <FaTrash size="20px" />
                                        </button>
                                    </div>
                                );
                            })
                        : null
                }
            </div>
        );
    };

    return (
        <>
            <div className="group-page-header">
                <div className="main-title">
                    Edit package information
                </div>
                <Row gap={8}>
                    <div style={{ flex: 1 }}>
                        <EditableInput
                            value={filePackage.name}
                            setValue={name => {
                                setFilePackage({ ...filePackage, name });
                            }}
                        />
                    </div>
                    {
                        data?.memberType === 'ADMIN' ? (
                            <button
                                style={{ flex: 0, minWidth: '150px' }}
                                className="my-button"
                                onClick={() => setCloneDialogOpen(true)}
                            >
                                CLONE
                            </button>
                        ) : <></>
                    }
                    <button
                        style={{ flex: 0, minWidth: '150px' }}
                        className="my-button"
                        onClick={() => setDeleteDialogOpen(true)}
                    >
                        DELETE
                    </button>
                </Row>
            </div>
            <div className="page-container" style={{ marginBottom: '32px' }}>
                <div className="edit-package-container">
                    <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div className="box basic-info" style={{ gap: '12px' }}>
                            <div className="info-title">Basic info</div>
                            <TextArea
                                label="Description"
                                placeholder="Enter description"
                                value={filePackage.description}
                                setValue={description => setFilePackage({ ...filePackage, description })}
                            />
                            <TextArea
                                label="Keypoints"
                                placeholder="Enter keypoints"
                                value={filePackage.keypoints}
                                setValue={keypoints => setFilePackage({ ...filePackage, keypoints })}
                            />
                            {
                                isAdmin && (
                                    <SelectField
                                        label="Type"
                                        placeholder="Select type"
                                        value={filePackage.type}
                                        options={TYPES}
                                        setValue={type => setFilePackage({ ...filePackage, type })}
                                    />
                                )
                            }
                            <MultiSelectField
                                label="Levels"
                                placeholder="Select levels"
                                value={filePackage.levels}
                                options={LEVELS.map(value => ({ value, text: value }))}
                                setValue={levels => setFilePackage({ ...filePackage, levels })}
                            />
                            {
                                isAdmin && (
                                    <MultiSelectField
                                        label="Categories"
                                        placeholder="Select categories"
                                        value={filePackage.categories}
                                        options={CATEGORIES.map(value => ({ value, text: value }))}
                                        setValue={categories => setFilePackage({ ...filePackage, categories })}
                                    />
                                )
                            }
                            {
                                isAdmin && (
                                    <TextField
                                        label="Price"
                                        placeholder="Enter value"
                                        value={filePackage.price ? String(filePackage.price) : ''}
                                        setValue={val => setFilePackage({ ...filePackage, price: Number(val) })}
                                        type="number"
                                    />
                                )
                            }
                            <div className="text-field">
                                <div className="label">Ready</div>
                                <Switch onChange={ready => setFilePackage({ ...filePackage, ready })} checked={filePackage.ready} />
                            </div>
                            {
                                isAdmin && (
                                    <div className="text-field">
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <div className="label">Thumbnail</div>
                                            <ImageUploading
                                                value={[]}
                                                onChange={onUploadThumbnail}
                                                maxNumber={100}
                                                dataURLKey="data_url"
                                            >
                                                {({
                                                      onImageUpload,
                                                      dragProps,
                                                  }) => (
                                                    <button
                                                        style={{ flex: 0, minWidth: '100px' }}
                                                        className="my-button"
                                                        onClick={onImageUpload}
                                                        {...dragProps}
                                                    >
                                                        Upload
                                                    </button>
                                                )}
                                            </ImageUploading>
                                        </div>
                                        <img
                                            style={{
                                                maxWidth: '100%'
                                            }}
                                            alt="Upload image"
                                            src={`https://api.hockeycoachvision.com/package/${id}/file/thumbnail?rand=${rand}`}
                                        />
                                    </div>
                                )
                            }
                        </div>
                        <button
                            style={{ flex: 0, minWidth: '150px' }}
                            className="my-button"
                            onClick={onSave}
                        >
                            SAVE
                        </button>
                        {data?.organisationType === 'ADMIN' && renderPreviews()}
                    </div>
                    <div className="files">
                        <Row style={{ marginBottom: '12px' }}>
                            <div className="subtitle">Files</div>
                            <button
                                style={{ flex: 0, minWidth: '100px' }}
                                className="my-button"
                                onClick={() => setAddDialogOpen(true)}
                            >
                                ADD
                            </button>
                        </Row>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext items={filePackage.files} strategy={rectSortingStrategy}>
                                {filePackage.files.map((item) => (
                                    <FileRow
                                        key={item.id}
                                        content={item}
                                        onRemove={() => {
                                        }}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                    </div>

                </div>
            </div>
            <ConfirmationDialog
                title="Delete package"
                description="Are you sure you want to delete this package?"
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={() => {
                    setDeleteDialogOpen(false);
                    onDelete();
                }}
            />
            <CloneDialog
                id={Number(id)}
                api={api}
                portals={portals}
                open={cloneDialogOpen}
                onClose={() => setCloneDialogOpen(false)}
            />
            <AddFilesDialog
                api={api}
                currentFiles={filePackage.files}
                portals={portals}
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                onAdd={(files) => {
                    setFilePackage({ ...filePackage, files: [...filePackage.files, ...files] });
                    setAddDialogOpen(false);
                }}
            />
        </>
    );
}

export default PackagePage;
